import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { ArrowLeft, Plus, Building, Trash, Pencil } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CompanyManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  
  // Form state
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [address, setAddress] = useState('');
  const [state, setState] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [logo, setLogo] = useState(null);
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankBranch, setBankBranch] = useState('');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/companies`, {
        withCredentials: true,
      });
      setCompanies(data);
    } catch (error) {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setShortCode('');
    setGstNumber('');
    setPanNumber('');
    setAddress('');
    setState('');
    setStateCode('');
    setLogo(null);
    setBankAccountName('');
    setBankAccountNumber('');
    setBankIfsc('');
    setBankName('');
    setBankBranch('');
    setEditingCompany(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData();
    formData.append('name', name);
    formData.append('short_code', shortCode);
    formData.append('gst_number', gstNumber);
    formData.append('pan_number', panNumber);
    formData.append('address', address);
    formData.append('state', state);
    formData.append('state_code', stateCode);
    formData.append('bank_account_name', bankAccountName);
    formData.append('bank_account_number', bankAccountNumber);
    formData.append('bank_ifsc', bankIfsc);
    formData.append('bank_name', bankName);
    formData.append('bank_branch', bankBranch);
    if (logo) {
      formData.append('logo', logo);
    }

    try {
      if (editingCompany) {
        await axios.put(
          `${BACKEND_URL}/api/companies/${editingCompany.id}`,
          formData,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
        toast.success('Company updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/companies`,
          formData,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
        toast.success('Company created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setName(company.name);
    setShortCode(company.short_code);
    setGstNumber(company.gst_number);
    setPanNumber(company.pan_number);
    setAddress(company.address);
    setState(company.state);
    setStateCode(company.state_code);
    setBankAccountName(company.bank_account_name || '');
    setBankAccountNumber(company.bank_account_number || '');
    setBankIfsc(company.bank_ifsc || '');
    setBankName(company.bank_name || '');
    setBankBranch(company.bank_branch || '');
    setDialogOpen(true);
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/companies/${companyId}`, {
        withCredentials: true,
      });
      toast.success('Company deleted successfully');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-pulse text-zinc-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3"
            data-testid="back-button"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
            Company Management
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Manage your company registrations (GST, PAN, Address)
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-zinc-800">Companies</h2>
            <p className="text-sm text-zinc-600 mt-1">{companies.length} total companies</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button
                className="bg-blue-700 hover:bg-blue-800 text-white rounded-none"
                data-testid="add-company-button"
              >
                <Plus size={20} className="mr-2" />
                Add Company
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-zinc-900">
                  {editingCompany ? 'Edit Company' : 'Add New Company'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                      Company Name *
                    </Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                      Short Code * (e.g., SGD)
                    </Label>
                    <Input
                      value={shortCode}
                      onChange={(e) => setShortCode(e.target.value.toUpperCase())}
                      required
                      maxLength={5}
                      placeholder="SGD"
                      className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                      data-testid="short-code-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                      State *
                    </Label>
                    <Input
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      required
                      placeholder="Karnataka"
                      className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                      data-testid="state-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                      State Code * (e.g., KA, TG)
                    </Label>
                    <Input
                      value={stateCode}
                      onChange={(e) => setStateCode(e.target.value.toUpperCase())}
                      required
                      maxLength={2}
                      placeholder="KA"
                      className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                      data-testid="state-code-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                      GST Number *
                    </Label>
                    <Input
                      value={gstNumber}
                      onChange={(e) => setGstNumber(e.target.value)}
                      required
                      className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                      data-testid="gst-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                      PAN Number *
                    </Label>
                    <Input
                      value={panNumber}
                      onChange={(e) => setPanNumber(e.target.value)}
                      required
                      className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                      data-testid="pan-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                    Address *
                  </Label>
                  <textarea
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                    rows={3}
                    className="mt-2 w-full bg-zinc-50 border border-zinc-200 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 rounded-none p-3 text-base"
                    data-testid="address-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                    Company Logo
                  </Label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLogo(e.target.files[0])}
                    className="mt-2 w-full bg-zinc-50 border border-zinc-200 focus:border-blue-600 rounded-none p-3 text-base"
                    data-testid="logo-input"
                  />
                </div>
                <div className="pt-2 border-t border-zinc-200">
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Bank Details (for invoices)</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Account Name</Label>
                      <Input value={bankAccountName} onChange={(e) => setBankAccountName(e.target.value)}
                        placeholder="As per bank records"
                        className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="bank-account-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Account Number</Label>
                      <Input value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)}
                        className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="bank-account-number-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Bank Name</Label>
                      <Input value={bankName} onChange={(e) => setBankName(e.target.value)}
                        className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="bank-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Branch</Label>
                      <Input value={bankBranch} onChange={(e) => setBankBranch(e.target.value)}
                        className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="bank-branch-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">IFSC Code</Label>
                      <Input value={bankIfsc} onChange={(e) => setBankIfsc(e.target.value)}
                        className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="bank-ifsc-input" />
                    </div>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-6"
                  data-testid="submit-company-button"
                >
                  {editingCompany ? 'Update Company' : 'Add Company'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {companies.length === 0 ? (
          <div className="border border-zinc-200 bg-white p-12 text-center">
            <Building size={64} className="mx-auto text-zinc-400 mb-4" />
            <p className="text-lg font-medium text-zinc-600">No companies yet</p>
            <p className="text-sm text-zinc-500 mt-2">Add your first company registration. Each company gets its own GST, logo, and auto-generated PI number series.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map((company) => (
              <div
                key={company.id}
                className="bg-white border border-zinc-200 p-6 hover:shadow-sm transition-shadow duration-200"
                data-testid={`company-card-${company.id}`}
              >
                {company.logo_url && (
                  <img
                    src={`${BACKEND_URL}/api${company.logo_url}`}
                    alt={company.name}
                    className="h-16 mb-4 object-contain"
                  />
                )}
                <h3 className="text-lg font-semibold text-zinc-800 mb-2">
                  {company.name} <span className="text-sm font-mono text-blue-700">({company.short_code})</span>
                </h3>
                <div className="text-sm text-zinc-600 space-y-1 mb-4">
                  <p><span className="font-medium">GST:</span> {company.gst_number}</p>
                  <p><span className="font-medium">PAN:</span> {company.pan_number}</p>
                  <p><span className="font-medium">State:</span> {company.state} ({company.state_code})</p>
                  <p className="text-xs mt-2">{company.address}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEdit(company)}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-zinc-300 rounded-none hover:bg-zinc-100"
                    data-testid={`edit-company-${company.id}`}
                  >
                    <Pencil size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(company.id)}
                    variant="outline"
                    size="sm"
                    className="border-red-300 text-red-700 rounded-none hover:bg-red-50"
                    data-testid={`delete-company-${company.id}`}
                  >
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
